// Detecção de offline (P0.9, ASM-070): eventos online/offline do sistema + teste de
// alcance do servidor a cada 3 s (o evento sozinho não percebe Wi-Fi sem internet).
// Pior caso para mudar o aviso: 3 s de intervalo + 2 s de tempo-limite do teste = 5 s.

export interface ConnectivityOptions {
  ping: () => Promise<boolean>;
  onChange: (online: boolean) => void;
  intervalMs?: number;
  pingTimeoutMs?: number;
  target?: Pick<Window, 'addEventListener' | 'removeEventListener'>;
  navigatorOnLine?: () => boolean;
}

export const CHECK_INTERVAL_MS = 3000;
export const PING_TIMEOUT_MS = 2000;

export function startConnectivityMonitor(opts: ConnectivityOptions): () => void {
  const interval = opts.intervalMs ?? CHECK_INTERVAL_MS;
  const timeout = opts.pingTimeoutMs ?? PING_TIMEOUT_MS;
  const target = opts.target ?? window;
  const onLine = opts.navigatorOnLine ?? (() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
  let online: boolean | null = null;
  let stopped = false;
  let checking = false;

  const set = (value: boolean) => {
    if (stopped || value === online) return;
    online = value;
    opts.onChange(value);
  };

  const pingWithTimeout = () =>
    new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => resolve(false), timeout);
      opts.ping().then(
        (ok) => {
          clearTimeout(timer);
          resolve(ok);
        },
        () => {
          clearTimeout(timer);
          resolve(false);
        }
      );
    });

  const check = async () => {
    if (checking) return;
    if (!onLine()) return set(false);
    checking = true;
    try {
      set(await pingWithTimeout());
    } finally {
      checking = false;
    }
  };

  const handleOffline = () => set(false);
  const handleOnline = () => void check();
  target.addEventListener('offline', handleOffline);
  target.addEventListener('online', handleOnline);
  const id = setInterval(() => void check(), interval);
  void check();

  return () => {
    stopped = true;
    clearInterval(id);
    target.removeEventListener('offline', handleOffline);
    target.removeEventListener('online', handleOnline);
  };
}
